import React, { useRef, useEffect, useMemo, useCallback } from "react";
import Typed from "typed.js";
import "./HeroStyles.css";
import linkedin from "../../assets/linkedin.png";
import github from "../../assets/github.png";
import sesini2 from "../../assets/sesini2.png";
import cvPdf from "../../assets/Navodya_Fernando.pdf";

// Cards that float in the dead zone between left text and right image
const MID_CARDS = [
  { icon: "</>",  label: "Clean Code",   sub: "SOLID Principles" },
  { icon: "{ }",  label: "API Design",   sub: "REST · gRPC"      },
  { icon: "⬡",   label: "Cloud Native", sub: "AWS · Azure"      },
  { icon: "∞",   label: "Enterprise",   sub: "Scalable Systems"  },
];

export const Hero = ({ setLoader }) => {
  const role       = useRef(null);
  const sectionRef = useRef(null);
  const glowRef    = useRef(null);
  const rafRef     = useRef(null);

  /* ── Typed.js ── */
  useEffect(() => {
    const typed = new Typed(role.current, {
      strings: [
        "Software Engineer.",
        "Backend Developer.",
        "Full Stack Developer.",
        "Cloud & DevOps Engineer.",
      ],
      typeSpeed: 75,
      backSpeed: 40,
      loop: true,
    });
    return () => typed.destroy();
  }, []);

  /* ── Mouse parallax ── */
  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const el = sectionRef.current;
      if (!el) { rafRef.current = null; return; }

      const rect = el.getBoundingClientRect();
      // Normalise to -1 … +1
      const mx = ((e.clientX - rect.left)  / rect.width  - 0.5) * 2;
      const my = ((e.clientY - rect.top)   / rect.height - 0.5) * 2;

      el.style.setProperty("--mx", mx.toFixed(3));
      el.style.setProperty("--my", my.toFixed(3));

      // Move cursor glow to exact pointer position
      if (glowRef.current) {
        glowRef.current.style.left    = `${e.clientX - rect.left}px`;
        glowRef.current.style.top     = `${e.clientY - rect.top}px`;
        glowRef.current.style.opacity = "1";
      }

      rafRef.current = null;
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = sectionRef.current;
    if (el) {
      el.style.setProperty("--mx", "0");
      el.style.setProperty("--my", "0");
    }
    if (glowRef.current) glowRef.current.style.opacity = "0";
  }, []);

  /* ── Background particles ── */
  const particles = useMemo(() =>
    Array.from({ length: 22 }, (_, i) => ({
      x:        ((i * 37 + 13) % 92) + 4,
      y:        ((i * 61 + 7)  % 85) + 5,
      size:     2 + (i % 3),
      delay:    (i * 0.38).toFixed(2),
      duration: (5 + (i % 5)).toFixed(1),
    })), []
  );

  return (
    <section
      id="hero"
      className="hero-section"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >

      {/* ── Animated Background ── */}
      <div className="hero-bg" aria-hidden="true">
        {/* Cursor-following glow */}
        <div ref={glowRef} className="cursor-glow" />

        <div className="hero-grid" />
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />

        {particles.map((p, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left:              `${p.x}%`,
              top:               `${p.y}%`,
              width:             `${p.size}px`,
              height:            `${p.size}px`,
              animationDelay:    `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>

      {/* ── Floating mid-section cards (fills the gap) ── */}
      <div className="hero-mid-cards" aria-hidden="true">
        {MID_CARDS.map((card, i) => (
          <div key={i} className={`mid-card mid-card-${i + 1}`}>
            <span className="mc-icon">{card.icon}</span>
            <span className="mc-body">
              <span className="mc-label">{card.label}</span>
              <span className="mc-sub">{card.sub}</span>
            </span>
          </div>
        ))}

        {/* Connecting SVG lines between cards */}
        <svg className="mid-lines" aria-hidden="true">
          <line className="mid-line" x1="0%" y1="0%" x2="100%" y2="100%" />
          <line className="mid-line mid-line-2" x1="100%" y1="20%" x2="0%" y2="80%" />
        </svg>
      </div>

      {/* ── Left: Text Content ── */}
      <div className="hero-content">
        <div className="hero-badge hero-anim" style={{ "--d": 0 }}>
          <span className="badge-dot" />
          Available for opportunities
        </div>

        <p className="hero-greeting hero-anim" style={{ "--d": 1 }}>Hello, I'm</p>

        <h1 className="hero-name hero-anim" style={{ "--d": 2 }}>
          <span className="name-word">Navodya</span>
          <br />
          <span className="name-word name-word-2">Fernando</span>
        </h1>

        <h2 className="hero-role hero-anim" style={{ "--d": 3 }}>
          <span ref={role} />
        </h2>

        <p className="hero-desc hero-anim" style={{ "--d": 4 }}>
          Software Engineer with 5+ years of experience delivering scalable,
          enterprise-grade applications across logistics, aviation, HR, and
          SaaS domains. Based in Dubai, UAE.
        </p>

        <div className="hero-btns hero-anim" style={{ "--d": 5 }}>
          <a className="hero-btn-primary" href={cvPdf} download="Navodya_Fernando-CV.pdf">
            <span>Download CV</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
          </a>
          <a href="#contact" onClick={() => setLoader(true)} className="hero-btn-outline">
            <span>Hire Me</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <div className="hero-socials hero-anim" style={{ "--d": 6 }}>
          <a href="https://www.linkedin.com/in/sesini/" className="hero-social-icon" aria-label="LinkedIn">
            <img src={linkedin} alt="LinkedIn" decoding="async" />
          </a>
          <a href="https://github.com/sesini1998" className="hero-social-icon" aria-label="GitHub">
            <img src={github} alt="GitHub" decoding="async" />
          </a>
          <div className="hero-scroll-hint">
            <div className="mouse-icon">
              <div className="mouse-wheel" />
            </div>
            <span className="scroll-label">Scroll</span>
          </div>
        </div>
      </div>

      {/* ── Right: Profile Visual ── */}
      <div className="hero-visual hero-anim" style={{ "--d": 2 }}>
        <div className="hero-img-outer">

          {/* Ambient glow */}
          <div className="hero-img-glow" />

          {/* Expanding pulse rings */}
          <div className="pulse-ring pulse-ring-1" />
          <div className="pulse-ring pulse-ring-2" />
          <div className="pulse-ring pulse-ring-3" />

          {/* Orbiting rings with glowing dots */}
          <div className="orbit-ring">
            <div className="orbit-dot" />
          </div>
          <div className="orbit-ring orbit-ring-2">
            <div className="orbit-dot orbit-dot-2" />
          </div>

          {/* Rotating conic-gradient border */}
          <div className="hero-img-ring" />

          {/* Static image container */}
          <div className="hero-img-frame">
            <img
              src={sesini2}
              alt="Navodya Fernando — Software Engineer based in Dubai, UAE"
              className="hero-img"
              fetchpriority="high"
              decoding="async"
            />
          </div>

          {/* Floating tech tags */}
          <div className="ftag ftag-1">.NET Core</div>
          <div className="ftag ftag-2">React</div>
          <div className="ftag ftag-3">Django</div>
          <div className="ftag ftag-4">Cloud ☁</div>
          <div className="ftag ftag-5">5+ Yrs</div>
          <div className="ftag ftag-6">NestJS</div>
          <div className="ftag ftag-7">NextJS</div>
        </div>

        {/* Stats strip */}
        <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-num">5<span className="stat-sup">+</span></span>
            <span className="stat-label">Years Exp.</span>
          </div>
          <div className="stat-sep" />
          <div className="stat-item">
            <span className="stat-num">10<span className="stat-sup">+</span></span>
            <span className="stat-label">Projects</span>
          </div>
          <div className="stat-sep" />
          <div className="stat-item">
            <span className="stat-num">2</span>
            <span className="stat-label">Countries</span>
          </div>
        </div>
      </div>
    </section>
  );
};
